/**
 * @fileoverview The share going out: the words that go with it (the caption
 * starter, else the season's words, or the name and variety) and the link,
 * Share (the browser's share sheet, the words on the clipboard too, as
 * Instagram on Android drops shared text) and Save picture (every picture,
 * one after another, the words copied). Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105); nothing in
 * it changed.
 *
 * Where: SharePlantDialog's Share screen.
 * Settings: SHARE_DOWNLOAD_GAP_MS, SHARE_DOWNLOAD_REVOKE_MS (config/tunables/plantList).
 * Words (en/es): plants.share.signoff, sharedDone, shareFailed, savedOne,
 * savedOneNoCaption, saved, savedNoCaption.
 *
 * @module shareOut
 */

import { canShareFiles } from '@/features/share/frames/buildFrames';
import { SHARE_DOWNLOAD_GAP_MS, SHARE_DOWNLOAD_REVOKE_MS } from '@/config/tunables/plantList';
import type { SharePlantDialogProps } from './shareStudioTypes';
import type { ShareStory } from './useShareStory';

/**
 * Share and Save picture, with the words and the link that go with them.
 *
 * @param props - The dialog's props (SharePlantDialogProps).
 * @param story - The story and the choices (useShareStory): its words and its error and saved lines.
 * @param frames - The finished pictures, or null while they are made.
 * @param link - The share's link, or null.
 * @param used - Saves the link and records how it went out (useShareLink).
 * @returns Whether the share sheet takes the pictures, the words, and the two handlers.
 */
export function useShareOut(
  props: SharePlantDialogProps, story: ShareStory, frames: File[] | null, link: string | null,
  used: (action: 'share' | 'save' | 'copy') => void,
): { shareSheet: boolean; caption: string; handleShare: () => Promise<void>; handleDownload: () => Promise<void> } {
  const { name, variety, season, captionStarter = '' } = props;
  const { t, setError, setSaved } = story;

  /* ------------------------------------------------------------ share */
  /** Whether this browser's share sheet takes the pictures. */
  const shareSheet = frames ? canShareFiles(frames) : false;
  /** The words that go with the share: the caption starter, else the season's words, or the plant's name and variety. */
  const caption = captionStarter.trim() || (season ? season.caption : `${name} — ${variety}`);
  /* The words and the link, for the share text and the clipboard: the link
     is what earns, and this is where it can be clicked. */
  const captionWithLink = link ? `${caption}\n${t('plants.share.signoff', { link })}` : caption;

  /** Hands the finished pictures and the words to the share sheet. */
  const handleShare = async (): Promise<void> => {
    if (!frames) return;
    setError(null);
    try {
      // The caption on the clipboard too: Instagram on Android drops the text a share carries. Not awaited, to keep the tap's go-ahead for the share.
      navigator.clipboard?.writeText(captionWithLink).catch(() => { /* no clipboard, no matter */ });
      await navigator.share({ files: frames, title: name, text: captionWithLink });
      used('share');
      // The sheet stays open on Share, to send the same pictures somewhere else too.
      setSaved(t('plants.share.sharedDone'));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // The browser's own words ("NotAllowedError: Permission denied") help
      // nobody; Save always works, so it is offered instead.
      console.error('Could not share:', err);
      setError(t('plants.share.shareFailed'));
    }
  };
  /** Saves every picture, one after another, and copies the words and link to go with them. */
  const handleDownload = async (): Promise<void> => {
    if (!frames) return;
    setError(null);
    setSaved(null);
    for (const [index, file] of frames.entries()) {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), SHARE_DOWNLOAD_REVOKE_MS);
      if (index < frames.length - 1) await new Promise((r) => setTimeout(r, SHARE_DOWNLOAD_GAP_MS));
    }
    used('save');
    let captionCopied = false;
    try {
      // Without a clipboard (an insecure page, an old browser) nothing was copied, so it must not say so.
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(captionWithLink);
        captionCopied = true;
      }
    } catch { /* no clipboard, no matter */ }
    setSaved(
      frames.length === 1
        ? t(captionCopied ? 'plants.share.savedOne' : 'plants.share.savedOneNoCaption')
        : t(captionCopied ? 'plants.share.saved' : 'plants.share.savedNoCaption', { count: frames.length })
    );
  };

  return { shareSheet, caption, handleShare, handleDownload };
}
